import authService from './authService';
import { ensureOk, readData, readList, toQuery } from './apiResponse';
import type { CacDocument, CacDocumentFilter, CacDocumentInput, ExpiringItem } from '../types/cac';

/** Acervo CAC → Documentos (CR, CRAF, GT, porte) e vencimentos. Exigem `features.cac`. */
class CacDocumentService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  /** GET /cac-documents?doc_type=&firearm_id=&status= (padrão: todos menos renovados). */
  async getAll(filter: CacDocumentFilter = {}): Promise<CacDocument[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/cac-documents${toQuery({ doc_type: filter.doc_type || undefined, firearm_id: filter.firearm_id, status: filter.status || undefined })}`,
    );
    return readList<CacDocument>(response, 'Falha ao listar os documentos CAC');
  }

  /** GET /cac-documents/expiring?days= — documentos CAC e documentos com validade, vencidos ou vencendo. */
  async getExpiring(days: number): Promise<ExpiringItem[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/cac-documents/expiring${toQuery({ days })}`);
    return readList<ExpiringItem>(response, 'Falha ao listar os vencimentos');
  }

  /** GET /cac-documents/:id — com `document` (arquivo) e `renewed_by`. */
  async getById(id: number): Promise<CacDocument> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/cac-documents/${id}`);
    return readData<CacDocument>(response, 'Falha ao carregar o documento CAC');
  }

  /** POST /cac-documents */
  async create(input: CacDocumentInput): Promise<CacDocument> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/cac-documents', input);
    return readData<CacDocument>(response, 'Falha ao cadastrar o documento CAC');
  }

  /** PUT /cac-documents/:id (409 se já renovado) */
  async update(id: number, input: CacDocumentInput): Promise<CacDocument> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/cac-documents/${id}`, input);
    return readData<CacDocument>(response, 'Falha ao atualizar o documento CAC');
  }

  /** POST /cac-documents/:id/renew — cria o novo e marca o antigo como renovado; devolve o novo. */
  async renew(id: number, input: CacDocumentInput): Promise<CacDocument> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/cac-documents/${id}/renew`, input);
    return readData<CacDocument>(response, 'Falha ao renovar o documento CAC');
  }

  /** DELETE /cac-documents/:id — exclusão lógica. */
  async remove(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/cac-documents/${id}`);
    await ensureOk(response, 'Falha ao excluir o documento CAC');
  }
}

export const cacDocumentService = new CacDocumentService();
export default cacDocumentService;
